import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import ReservationExportButton from "@/components/ReservationExportButton";
import ResponsiveSheet from "@/components/ResponsiveSheet";
import { GuestMergeCompare } from "@/components/GuestMergeCompare";
import { GuestSyncDialog } from "@/components/GuestSyncDialog";
import { ListHeader, ListToolbar, ToolbarButton, FilterPanel, FilterSelect } from "@/components/ListPage";
import {
  MagnifyingGlass,
  X,
  CircleNotch,
  UsersThree,
  Users,
  User,
  Buildings,
  Plus,
  Trash,
  FunnelSimple,
  CheckCircle,
  Check,
  XCircle,
} from "@phosphor-icons/react";

export default function GuestsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [showFilters, setShowFilters] = useState(() => window.innerWidth >= 768);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tp, setTp] = useState("");
  const [country, setCountry] = useState("");
  const [wh, setWh] = useState("");
  const [sort, setSort] = useState("created");
  const [openId, setOpenId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  // Auto-open a guest profile when navigating with `?open=<guest_id>` in the URL.
  // Used by the reservation drawer's "Profil gosta" link so users can jump to a
  // guest's full history without losing the URL context (back button works).
  useEffect(() => {
    const openParam = searchParams.get("open");
    if (openParam && openParam !== openId) {
      setOpenId(openParam);
    }
  }, [searchParams, openId]);

  const toggleSel = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = (all) => setSelected(all ? new Set(rows.map((r) => r.id)) : new Set());

  const deleteGuest = async (id, name) => {
    if (!window.confirm(`Obrisati gosta ${name || ""}? Ova akcija se ne može poništiti.`)) return;
    try {
      await api.delete(`/guests/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
      setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
      toast.success("Gost obrisan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Greška pri brisanju");
    }
  };

  const [mergeOpen, setMergeOpen] = useState(false);
  const selectDuplicates = (g, e) => {
    e.stopPropagation();
    setSelected(new Set([g.id, ...(g.possible_duplicate_ids || [])]));
    toast.info("Označeni su gosti s istim imenom — provjeri i klikni Spoji.");
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Obrisati ${ids.length} odabranih gostiju? Ova akcija se ne može poništiti.`)) return;
    let ok = 0, fail = 0;
    await Promise.all(ids.map(async (id) => {
      try { await api.delete(`/guests/${id}`); ok += 1; } catch { fail += 1; }
    }));
    setRows((prev) => prev.filter((r) => !selected.has(r.id)));
    setSelected(new Set());
    if (fail === 0) toast.success(`Obrisano ${ok} gostiju`);
    else toast.error(`Obrisano ${ok}, neuspješno ${fail}`);
  };

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set("search", q);
      if (tp === "guest") p.set("is_company", "false");
      if (tp === "company") p.set("is_company", "true");
      if (country) p.set("country", country);
      if (wh) p.set("would_host_again", wh);
      if (sort) p.set("sort", sort);
      const { data } = await api.get(`/guests?${p.toString()}`);
      setRows(data || []);
    } catch {
      toast.error("Greška pri učitavanju gostiju");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tp, country, wh, sort]);

  const clearFilters = () => { setQ(""); setTp(""); setCountry(""); setWh(""); setSort("created"); };
  const totals = useMemo(() => ({
    count: rows.length,
    guests: rows.filter((r) => !r.is_company).length,
    companies: rows.filter((r) => r.is_company).length,
  }), [rows]);

  return (
    <div className="rw-page" data-testid="guests-page">
      <ListHeader
        title="Gosti i tvrtke"
        summary={<>{totals.guests} gostiju · {totals.companies} tvrtki</>}
      />
      <ListToolbar
        value={q}
        onChange={setQ}
        onSubmit={load}
        placeholder="Traži ime, email, OIB, dokument..."
        filtersOpen={showFilters}
        onToggleFilters={() => setShowFilters((v) => !v)}
        filterCount={[tp, country, wh, sort !== "created" ? sort : ""].filter(Boolean).length}
        testIdPrefix="guests"
      >
        <ReservationExportButton />
        <ToolbarButton icon={Plus} label="Novi gost" onClick={() => setShowCreate(true)} testId="guest-create-btn" primary />
      </ListToolbar>
      <FilterPanel open={showFilters} onReset={clearFilters} onApply={load} canReset={!!(tp || country || wh || sort !== "created")} testIdPrefix="guests">
        <FilterSelect value={tp} onChange={setTp} testId="guests-filter-type" options={[
          { value: "", label: "Gosti i tvrtke" }, { value: "guest", label: "Samo gosti" }, { value: "company", label: "Samo tvrtke" },
        ]} />
        <input className="rw-control" placeholder="Država" value={country} onChange={(e) => setCountry(e.target.value)} data-testid="guests-filter-country" />
        <FilterSelect value={wh} onChange={setWh} testId="guests-filter-would-host" options={[
          { value: "", label: "Ugosti opet: svi" }, { value: "true", label: "Ugosti opet: da" }, { value: "false", label: "Ugosti opet: ne" },
        ]} />
        <FilterSelect value={sort} onChange={setSort} testId="guests-filter-sort" options={[
          { value: "created", label: "Najnoviji" }, { value: "name", label: "Prezime A→Z" }, { value: "stays", label: "Broj boravaka" }, { value: "spent", label: "Potrošeno" }, { value: "last_stay", label: "Zadnji boravak" },
        ]} />
      </FilterPanel>
      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm dark:border-rose-900/50 dark:bg-rose-950/30">
          <span className="text-rose-700 dark:text-rose-300">{selected.size} odabrano</span>
          <div className="flex items-center gap-2">
          {selected.size >= 2 && (
            <button onClick={() => setMergeOpen(true)} className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-semibold text-primary-foreground hover:bg-primary/90" data-testid="guests-bulk-merge">
              <UsersThree size={13} weight="bold" /> Spoji
            </button>
          )}
          <button onClick={deleteSelected} className="inline-flex h-8 items-center gap-1.5 rounded-md bg-rose-600 px-3 text-xs font-semibold text-white hover:bg-rose-700" data-testid="guests-bulk-delete">
            <Trash size={13} weight="bold" /> Obriši
          </button>
          </div>
        </div>
      )}
      {mergeOpen && (
        <MergeGuestsSheet
          ids={[...selected]}
          onClose={() => setMergeOpen(false)}
          onMerged={() => { setMergeOpen(false); setSelected(new Set()); load(); }}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-16"><CircleNotch size={22} className="animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-lg border border-dashed border-border">
          <Users size={56} weight="thin" className="text-muted-foreground/40" />
          <p className="mt-3 text-sm text-muted-foreground">Nema gostiju za odabrane filtere</p>
        </div>
      ) : (
        <>
        <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <th className="w-8 px-2 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={rows.length > 0 && selected.size === rows.length}
                    onChange={(e) => toggleAll(e.target.checked)}
                    className="h-4 w-4"
                    data-testid="guests-select-all"
                  />
                </th>
                <th className="w-6 px-2 py-2"></th>
                <th className="px-3 py-2 text-left font-semibold">Ime / Naziv</th>
                <th className="px-3 py-2 text-left font-semibold">Kontakt</th>
                <th className="px-3 py-2 text-left font-semibold">Država</th>
                <th className="px-3 py-2 text-right font-semibold">Boravaka</th>
                <th className="px-3 py-2 text-right font-semibold">Potrošeno</th>
                <th className="px-3 py-2 text-left font-semibold">Zadnji boravak</th>
                <th className="px-3 py-2 text-center font-semibold">Ugosti opet</th>
                <th className="px-3 py-2 text-center font-semibold">Akcije</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((g) => (
                <tr key={g.id} onClick={() => setOpenId(g.id)} className={`cursor-pointer transition ${selected.has(g.id) ? "bg-primary/10" : "hover:bg-muted/30"}`} data-testid={`guest-row-${g.id}`}>
                  <td className="px-2 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(g.id)}
                      onChange={() => toggleSel(g.id)}
                      className="h-4 w-4"
                      data-testid={`guest-select-${g.id}`}
                    />
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    {g.is_company ? <Buildings size={16} weight="duotone" className="mx-auto text-primary" /> : <User size={16} weight="duotone" className="mx-auto text-primary" />}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-foreground">
                        {g.is_company ? (g.company_name || `${g.first_name || ""} ${g.last_name || ""}`) : `${g.first_name || ""} ${g.last_name || ""}`.trim()}
                      </p>
                      {(g.possible_duplicate_ids || []).length > 0 && (
                      <button type="button" onClick={(e) => selectDuplicates(g, e)} title="Isto ime i prezime kao drugi gost — klikni za označavanje" className="shrink-0 whitespace-nowrap rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 hover:bg-amber-500/25 dark:text-amber-300" data-testid={`guest-dup-${g.id}`}>Mogući duplikat</button>
                    )}
                    </div>
                    {g.oib && <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">OIB: {g.oib}</p>}
                  </td>
                  <td className="px-3 py-2.5 text-xs">
                    {g.email && <p className="text-foreground">{g.email}</p>}
                    {g.phone && <p className="text-muted-foreground">{g.phone}</p>}
                  </td>
                  <td className="px-3 py-2.5">{g.country || g.country_of_residence || "—"}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold">{g.total_stays || 0}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{Number(g.total_spent || 0).toFixed(2)} €</td>
                  <td className="px-3 py-2.5 text-xs">{g.last_stay || "—"}</td>
                  <td className="px-3 py-2.5 text-center">
                    {g.would_host_again === true && <CheckCircle size={18} weight="fill" className="mx-auto text-emerald-500" />}
                    {g.would_host_again === false && <XCircle size={18} weight="fill" className="mx-auto text-rose-500" />}
                  </td>
                  <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => deleteGuest(g.id, `${g.first_name || ""} ${g.last_name || ""}`.trim() || g.company_name)}
                      className="rounded p-1.5 text-muted-foreground hover:bg-rose-50 hover:text-rose-600"
                      title="Obriši gosta"
                      data-testid={`guest-delete-${g.id}`}
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card view */}
        <div className="space-y-2 md:hidden" data-testid="guests-mobile-cards">
          {rows.map((g) => {
            const label = g.is_company
              ? (g.company_name || `${g.first_name || ""} ${g.last_name || ""}`.trim())
              : `${g.first_name || ""} ${g.last_name || ""}`.trim();
            return (
              <div
                key={g.id}
                onClick={() => setOpenId(g.id)}
                className={`flex cursor-pointer items-center gap-3 rounded-lg border bg-card px-3 py-3 transition active:scale-[0.99] ${selected.has(g.id) ? "border-primary bg-primary/5" : "border-border"}`}
                data-testid={`guest-card-${g.id}`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(g.id)}
                  onChange={() => toggleSel(g.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="h-4 w-4 shrink-0"
                  data-testid={`guest-card-select-${g.id}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {g.is_company && <Buildings size={14} weight="duotone" className="shrink-0 text-primary" />}
                    <p className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{label || "—"}</p>
                    {(g.possible_duplicate_ids || []).length > 0 && (
                      <button type="button" onClick={(e) => selectDuplicates(g, e)} title="Isto ime i prezime kao drugi gost — klikni za označavanje" className="shrink-0 whitespace-nowrap rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 hover:bg-amber-500/25 dark:text-amber-300" data-testid={`guest-dup-${g.id}`}>Duplikat?</button>
                    )}
                    {g.would_host_again === true && <CheckCircle size={14} weight="fill" className="shrink-0 text-emerald-500" />}
                    {g.would_host_again === false && <XCircle size={14} weight="fill" className="shrink-0 text-rose-500" />}
                    <span className="shrink-0 font-mono text-sm font-semibold text-foreground">€{Number(g.total_spent || 0).toFixed(0)}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="min-w-0 flex-1 truncate">{g.email || g.phone || "—"}</span>
                    <span className="shrink-0">{g.country || "—"} · {g.total_stays || 0} {(g.total_stays || 0) === 1 ? "boravak" : "boravaka"}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {openId && <GuestPopup guestId={openId} onClose={() => {
        setOpenId(null);
        // Clean the ?open= param so the drawer doesn't re-open on refresh.
        if (searchParams.get("open")) {
          const next = new URLSearchParams(searchParams);
          next.delete("open");
          setSearchParams(next, { replace: true });
        }
        load();
      }} />}
      {showCreate && <GuestPopup isNew onClose={(created) => { setShowCreate(false); if (created?.id) setOpenId(created.id); load(); }} />}
    </div>
  );
}

export function GuestPopup({ guestId, isNew, onClose }) {
  const [tab, setTab] = useState("info");
  const [guest, setGuest] = useState(isNew ? emptyGuest() : null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/guests/${guestId}/statistics`);
        setGuest(data.guest);
        setStats(data);
      } catch {
        toast.error("Greška pri učitavanju");
      } finally {
        setLoading(false);
      }
    })();
  }, [guestId, isNew]);

  const update = (patch) => setGuest((g) => ({ ...g, ...patch }));
  const [syncItems, setSyncItems] = useState(null);

  const save = async () => {
    if (guest.is_company && (!(guest.company_name || "").trim() || !(guest.company_oib || "").trim())) {
      toast.error("Za tvrtku su obavezni naziv tvrtke i OIB.");
      return;
    }
    setSaving(true);
    const body = guest.is_company ? { ...guest, first_name: guest.company_name, last_name: "" } : guest;
    try {
      if (isNew) {
        const { data } = await api.post("/guests", { ...stripEmpty(body), last_name: body.last_name || "" });
        toast.success(guest.is_company ? "Tvrtka je kreirana" : "Gost je kreiran");
        onClose(data);
      } else {
        const { data } = await api.put(`/guests/${guestId}`, Object.fromEntries(Object.entries(body).map(([k, v]) => [k, k === "last_name" ? v || "" : v === "" ? null : v])));
        setGuest(data);
        toast.success("Spremljeno");
        api.get(`/guests/${guestId}/reservation-diff`).then((r) => { if (r.data?.reservations?.length) setSyncItems(r.data.reservations); }).catch(() => {});
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Greška pri spremanju");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm(`Obrisati gosta ${guest.first_name} ${guest.last_name}? Ova akcija se ne može poništiti.`)) return;
    try {
      await api.delete(`/guests/${guestId}`);
      toast.success("Obrisano");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Greška pri brisanju");
    }
  };

  if (loading || !guest) {
    return createPortal(
      <div className="rw-modal-overlay z-[80]" onClick={onClose}>
        <div className="flex h-72 w-80 items-center justify-center rounded-xl border border-border bg-card shadow-2xl">
          <CircleNotch size={22} className="animate-spin text-primary" />
        </div>
      </div>,
      document.body,
    );
  }

  const personName = `${guest.first_name || ""} ${guest.last_name || ""}`.trim();
  const title = guest.is_company
    ? (guest.company_name || "Nova tvrtka")
    : personName || "Novi gost";

  return createPortal(
    <div className="rw-modal-overlay z-[80]" onMouseDown={(e) => e.target === e.currentTarget && onClose()} data-testid="guest-popup">
      <div className="rw-modal-panel-lg" onMouseDown={(e) => e.stopPropagation()}>
        <div className="rw-modal-header">
          <div className="flex min-w-0 items-center gap-2">
            {guest.is_company ? <Buildings size={20} weight="duotone" className="shrink-0 text-primary" /> : <User size={20} weight="duotone" className="shrink-0 text-primary" />}
            <h2 className="truncate text-base font-semibold text-foreground sm:text-lg" data-testid="guest-popup-title">{title}</h2>
            {!isNew && guest.would_host_again === true && <span className="rw-badge bg-emerald-100 text-emerald-800">Preferirani</span>}
            {!isNew && guest.would_host_again === false && <span className="rw-badge bg-rose-100 text-rose-800">Blacklist</span>}
          </div>
          <button onClick={onClose} className="rw-btn-icon" data-testid="guest-popup-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        {!isNew && (
          <div className="flex border-b border-border bg-muted/20">
            <TabButton active={tab === "info"} onClick={() => setTab("info")} tid="guest-tab-info">{guest.is_company ? "Podaci o tvrtki" : "Podaci o gostu"}</TabButton>
            <TabButton active={tab === "stats"} onClick={() => setTab("stats")} tid="guest-tab-stats">Statistika</TabButton>
          </div>
        )}

        <div className="rw-modal-body">
          {tab === "info" || isNew ? (
            <div className="grid grid-cols-2 gap-x-3 gap-y-4">
              {isNew && (
                <div className="col-span-2 flex overflow-hidden rounded-md border border-border text-sm font-semibold" data-testid="guest-type-switch">
                  <button type="button" onClick={() => update({ is_company: false })} className={`flex-1 px-3 py-1.5 ${!guest.is_company ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground"}`} data-testid="guest-type-person">Fizička osoba</button>
                  <button type="button" onClick={() => update({ is_company: true })} className={`flex-1 px-3 py-1.5 ${guest.is_company ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground"}`} data-testid="guest-type-company">Tvrtka</button>
                </div>
              )}
              {guest.is_company ? <CompanyFields guest={guest} update={update} /> : <PersonFields guest={guest} update={update} />}
              <F col={2} label="Napomena"><textarea rows={2} className="gp-input" value={guest.notes || ""} onChange={(e) => update({ notes: e.target.value })} data-testid="guest-notes" placeholder="Slobodni tekst..." /></F>
            </div>
          ) : (
            <GuestStats stats={stats} />
          )}
        </div>

        <div className="rw-modal-footer">
          {!isNew ? (
            <button onClick={del} className="rw-btn-danger rw-btn-sm mr-auto" data-testid="guest-delete">
              <Trash size={12} weight="bold" /> Obriši
            </button>
          ) : <span className="mr-auto" />}
          <button onClick={onClose} className="rw-btn-secondary rw-btn-sm" data-testid="guest-popup-cancel">Odustani</button>
          <button onClick={save} disabled={saving} className="rw-btn-primary rw-btn-sm" data-testid="guest-save">
            {saving ? <CircleNotch size={12} className="animate-spin" /> : <Check size={12} weight="bold" />}
            {saving ? "Spremam..." : "Spremi"}
          </button>
        </div>
      </div>
      {syncItems && <GuestSyncDialog guestId={guestId} items={syncItems} onDone={() => setSyncItems(null)} />}
      <style>{`
        .gp-input {
          width: 100%;
          border: 1px solid hsl(var(--input));
          border-radius: 6px;
          background: hsl(var(--background));
          padding: 6px 10px;
          font-size: 13px;
          color: hsl(var(--foreground));
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .gp-input:focus {
          outline: none;
          border-color: hsl(var(--primary));
          box-shadow: 0 0 0 2px hsl(var(--primary) / 0.2);
        }
      `}</style>
    </div>,
    document.body,
  );
}

function GuestStats({ stats }) {
  if (!stats) return null;
  return (
    <div className="space-y-6" data-testid="guest-stats">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Boravaka" value={stats.total_stays} />
        <StatCard label="Noći ukupno" value={stats.total_nights} />
        <StatCard label="Noći prosjek" value={stats.avg_nights} />
        <StatCard label="Potrošeno" value={`${Number(stats.total_spent).toFixed(2)} €`} />
      </div>
      <div>
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">Prošli boravci</p>
        {stats.reservations.length === 0 ? (
          <p className="text-sm italic text-muted-foreground">Nema zabilježenih boravaka.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="bg-muted/40 text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2 text-left">Broj</th>
                  <th className="px-3 py-2 text-left">Prijava</th>
                  <th className="px-3 py-2 text-left">Odjava</th>
                  <th className="px-3 py-2 text-right">Noći</th>
                  <th className="px-3 py-2 text-right">Iznos</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {stats.reservations.map((r) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                    <td className="px-3 py-2">{r.check_in}</td>
                    <td className="px-3 py-2">{r.check_out}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.nights}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(r.total_amount || 0).toFixed(2)} {r.currency || "EUR"}</td>
                    <td className="px-3 py-2 text-xs">{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rw-card p-3">
      <p className="rw-overline">{label}</p>
      <p className="mt-1 font-mono text-lg font-bold text-foreground">{value}</p>
    </div>
  );
}

function TabButton({ active, onClick, tid, children }) {
  return (
    <button onClick={onClick} data-testid={tid} className={`relative px-4 py-2.5 text-sm font-semibold uppercase tracking-wide transition sm:px-5 ${active ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
      {children}
      {active && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-primary" />}
    </button>
  );
}

let _refCache = null;
function useRefData() {
  const [data, setData] = useState(_refCache);
  useEffect(() => {
    if (_refCache) return;
    Promise.all([api.get("/evisitor/countries"), api.get("/evisitor/document-types")])
      .then(([c, d]) => { _refCache = { countries: c.data || [], docs: d.data || { hr: [], foreign: [] } }; setData(_refCache); })
      .catch(() => {});
  }, []);
  return data || { countries: [], docs: { hr: [], foreign: [] } };
}

export function CountrySelect({ value, onChange, testId, className = "gp-input" }) {
  const { countries } = useRefData();
  const v = (value || "").toUpperCase();
  const known = countries.some((c) => c.code === v);
  return (
    <select className={className} value={v} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      <option value="">—</option>
      {v && !known && <option value={v}>{v}</option>}
      {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
    </select>
  );
}


function PersonFields({ guest, update }) {
  return (
    <>
      <F label="Ime"><input className="gp-input" value={guest.first_name || ""} onChange={(e) => update({ first_name: e.target.value })} data-testid="guest-first-name" /></F>
      <F label="Prezime"><input className="gp-input" value={guest.last_name || ""} onChange={(e) => update({ last_name: e.target.value })} data-testid="guest-last-name" /></F>
      <F col={2} label="Adresa"><input className="gp-input" value={guest.address || ""} onChange={(e) => update({ address: e.target.value })} data-testid="guest-address" /></F>
      <F label="Poštanski broj"><input className="gp-input" value={guest.postal_code || ""} onChange={(e) => update({ postal_code: e.target.value })} data-testid="guest-postal" /></F>
      <F label="Grad"><input className="gp-input" value={guest.city || ""} onChange={(e) => update({ city: e.target.value })} data-testid="guest-city" /></F>
      <F label="Država"><CountrySelect value={guest.country} onChange={(v) => update({ country: v })} testId="guest-country" /></F>
      <F label="OIB (neobavezno)"><input className="gp-input" value={guest.oib || ""} onChange={(e) => update({ oib: e.target.value })} data-testid="guest-oib" /></F>
      <F label="Kontakt telefon"><input className="gp-input" value={guest.phone || ""} onChange={(e) => update({ phone: e.target.value })} data-testid="guest-phone" /></F>
      <F label="Email"><input type="email" className="gp-input" value={guest.email || ""} onChange={(e) => update({ email: e.target.value })} data-testid="guest-email" /></F>
      <F label="Ugostili bismo opet?">
        <select className="gp-input" value={guest.would_host_again === true ? "true" : guest.would_host_again === false ? "false" : ""} onChange={(e) => update({ would_host_again: e.target.value === "" ? null : e.target.value === "true" })} data-testid="guest-would-host">
          <option value="">—</option>
          <option value="true">Da</option>
          <option value="false">Ne</option>
        </select>
      </F>
      <F col={2} label="Dojam o gostu"><textarea rows={3} className="gp-input" value={guest.impression || ""} onChange={(e) => update({ impression: e.target.value })} data-testid="guest-impression" placeholder="Interni komentari o gostu (ponašanje, urednost, komunikacija...)" /></F>
    </>
  );
}

function CompanyFields({ guest, update }) {
  return (
    <>
      <F col={2} label="Naziv tvrtke *"><input className="gp-input" value={guest.company_name || ""} onChange={(e) => update({ company_name: e.target.value })} data-testid="guest-company-name" /></F>
      <F col={2} label="Adresa"><input className="gp-input" value={guest.company_address || ""} onChange={(e) => update({ company_address: e.target.value })} data-testid="guest-company-address" /></F>
      <F label="Poštanski broj"><input className="gp-input" value={guest.company_postal_code || ""} onChange={(e) => update({ company_postal_code: e.target.value })} data-testid="guest-company-postal" /></F>
      <F label="Grad"><input className="gp-input" value={guest.company_city || ""} onChange={(e) => update({ company_city: e.target.value })} data-testid="guest-company-city" /></F>
      <F label="Država"><CountrySelect value={guest.company_country} onChange={(v) => update({ company_country: v })} testId="guest-company-country" /></F>
      <F label="OIB tvrtke *"><input className="gp-input" value={guest.company_oib || ""} onChange={(e) => update({ company_oib: e.target.value })} data-testid="guest-company-oib" /></F>
      <F label="Email (neobavezno)"><input type="email" className="gp-input" value={guest.email || ""} onChange={(e) => update({ email: e.target.value })} data-testid="guest-email" /></F>
      <F label="Telefon (neobavezno)"><input className="gp-input" value={guest.phone || ""} onChange={(e) => update({ phone: e.target.value })} data-testid="guest-phone" /></F>
    </>
  );
}

function F({ label, col = 1, children }) {
  return (
    <label className={`flex min-w-0 flex-col justify-end ${col === 2 ? "col-span-2" : ""}`}>
      <span className="rw-field-label">{label}</span>
      {children}
    </label>
  );
}

function stripEmpty(o) {
  const out = {};
  Object.keys(o).forEach((k) => {
    const v = o[k];
    if (v === "" || v === undefined) return;
    out[k] = v;
  });
  return out;
}

function emptyGuest() {
  return {
    first_name: "", last_name: "", email: "", phone: "", is_company: false,
    company_name: "", address: "", city: "", postal_code: "", country: "",
    country_of_birth: "", city_of_birth: "", nationality: "", date_of_birth: "", gender: "",
    document_type: "", document_number: "", document_country: "", oib: "",
    impression: "", would_host_again: null, notes: "",
  };
}

function MergeGuestsSheet({ ids, onClose, onMerged }) {
  const [guests, setGuests] = useState([]);
  const [primary, setPrimary] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    Promise.all(ids.map((id) => api.get(`/guests/${id}`).then((r) => r.data).catch(() => null)))
      .then(async (list) => {
        const rows = list.filter(Boolean);
        const withStats = await Promise.all(rows.map((g) => api.get(`/guests/${g.id}/statistics`).then((r) => ({ ...g, _stats: r.data })).catch(() => g)));
        setGuests(withStats);
        const best = [...withStats].sort((a, b) => (b._stats?.total_stays || 0) - (a._stats?.total_stays || 0) || String(a.created_at).localeCompare(String(b.created_at)))[0];
        setPrimary(best?.id || null);
      });
  }, [ids.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const doMerge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/guests/merge", { primary_id: primary, merge_ids: ids });
      toast.success(`Spojeno ${data.merged + 1} gostiju · ${data.guest.total_stays} boravaka`);
      onMerged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Spajanje nije uspjelo");
    } finally { setBusy(false); }
  };
  return (
    <ResponsiveSheet
      onClose={onClose}
      title="Spoji goste"
      subtitle="Rezervacije, računi i statistike prelaze na odabranog gosta"
      width={Math.min(1200, 160 + 210 * Math.max(2, ids.length))}
      testId="guests-merge-sheet"
      footer={
        <>
          <button onClick={onClose} className="ml-auto h-10 rounded-md border border-border px-4 text-sm font-medium hover:bg-muted">Odustani</button>
          <button onClick={doMerge} disabled={!primary || busy} className="inline-flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50" data-testid="guests-merge-confirm">
            {busy ? <CircleNotch size={14} className="animate-spin" /> : <UsersThree size={14} weight="bold" />} Spoji
          </button>
        </>
      }
    >
      <div className="space-y-2 p-4 sm:p-5">
        <p className="text-xs text-muted-foreground">Usporedi podatke i odaberi glavni zapis. Žuto označeni redovi se razlikuju — prazna polja glavnog zapisa popunit će se iz ostalih.</p>
        {guests.length === 0 && <div className="flex justify-center py-6"><CircleNotch size={20} className="animate-spin text-muted-foreground" /></div>}
        {guests.length > 0 && <GuestMergeCompare guests={guests} primary={primary} setPrimary={setPrimary} />}
      </div>
    </ResponsiveSheet>
  );
}
